import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { QrCode, Cloud, BatteryCharging, Gauge, ReceiptText, Check, RotateCcw, ChevronDown } from "lucide-react";

const SERIAL = "#12A6AA";

const STEPS = [
  {
    key: "scan", icon: QrCode, label: "Scan serial", detail: `Scanning ${SERIAL}`,
    meaning: "Every ChargeRevo device — laptop receiver, phone case, sensor — carries a unique serial number. Scanning it in the app identifies exactly which device is asking for power.",
    next: "The serial is sent to the cloud to be matched against your account and plan.",
  },
  {
    key: "token", icon: Cloud, label: "Cloud entitlement", detail: "Cloud issues a signed charging token",
    meaning: "The cloud confirms the serial belongs to an active plan, then issues a signed entitlement token — permission to charge. It's data, never power.",
    next: "That token is handed to your nearby transmitter, which now knows this device is allowed to charge.",
  },
  {
    key: "charge", icon: BatteryCharging, label: "Charging", detail: "Transmitter authenticated · power flowing",
    meaning: "With permission verified locally, the transmitter delivers power through the surface into the device — no internet needed from this point on.",
    next: "As energy flows, the transmitter counts every watt-hour it delivers to the device.",
  },
  {
    key: "meter", icon: Gauge, label: "Metering", detail: "Every watt-hour metered to the serial",
    meaning: "Each watt-hour is attributed to that exact serial — wherever in the world it charged — so usage stays accurate across every device and location.",
    next: "Metered totals sync back to the cloud the next time there's a connection.",
  },
  {
    key: "invoice", icon: ReceiptText, label: "Invoice line", detail: "Usage appears on your account",
    meaning: "Your synced usage becomes a single line on your monthly statement — one account covering every device you own.",
    next: "The loop is complete: scan once, and every future charge is authorized, metered, and billed automatically.",
  },
];

export default function ActivationSimulator() {
  const [step, setStep] = useState(-1); // -1 idle
  const [openKey, setOpenKey] = useState(null);
  const [battery, setBattery] = useState(38);
  const [wh, setWh] = useState(0);
  const [running, setRunning] = useState(false);
  const timers = useRef([]);

  const clearTimers = () => { timers.current.forEach(clearTimeout); timers.current = []; };
  useEffect(() => () => clearTimers(), []);

  const start = () => {
    clearTimers();
    setRunning(true);
    setBattery(38);
    setWh(0);
    setStep(0);
    STEPS.forEach((_, i) => {
      timers.current.push(setTimeout(() => setStep(i), i * 1400));
    });
    // charging animation
    timers.current.push(setTimeout(() => {
      const iv = setInterval(() => {
        setBattery((b) => {
          const nb = Math.min(96, b + 2);
          return nb;
        });
        setWh((w) => Math.min(41.6, +(w + 1.2).toFixed(1)));
      }, 120);
      timers.current.push(setTimeout(() => clearInterval(iv), 4200));
    }, 2 * 1400));
    timers.current.push(setTimeout(() => setRunning(false), STEPS.length * 1400 + 400));
  };

  const reset = () => { clearTimers(); setStep(-1); setBattery(38); setWh(0); setRunning(false); };

  return (
    <div className="overflow-hidden rounded-3xl border border-white/10 bg-navy-deep" data-testid="poc-activation-simulator">
      <div className="grid lg:grid-cols-2">
        {/* Left: steps */}
        <div className="border-b border-white/10 p-7 md:p-10 lg:border-b-0 lg:border-r">
          <p className="font-display text-xs font-bold uppercase tracking-[0.25em] text-revo-green">POC · Activation simulator</p>
          <h3 className="mt-2 font-display text-3xl font-extrabold text-white">From box to charging in five minutes.</h3>
          <p className="mt-3 text-sm leading-relaxed text-revo-ice">
            Watch the patent-pending loop end to end: scan a serial, the cloud issues permission, power flows locally, every watt-hour is metered and billed.
          </p>

          <div className="mt-8 space-y-3">
            {STEPS.map((s, i) => {
              const active = step === i;
              const done = step > i;
              const open = openKey === s.key;
              const Icon = s.icon;
              return (
                <div
                  key={s.key}
                  data-testid={`activation-step-${s.key}`}
                  className={`overflow-hidden rounded-xl border transition-colors duration-300 ${
                    active ? "border-revo-green/60 bg-revo-green/10" : open ? "border-revo-green/40 bg-white/[0.04]" : done ? "border-white/10 bg-white/[0.03]" : "border-white/5"
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => setOpenKey(open ? null : s.key)}
                    data-testid={`activation-step-toggle-${s.key}`}
                    aria-expanded={open}
                    className="flex w-full items-center gap-4 p-3 text-left"
                  >
                    <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${active ? "bg-revo-green text-navy charge-pulse" : done ? "bg-revo-green/20 text-revo-green" : "bg-white/5 text-revo-ice/50"}`}>
                      {done ? <Check className="h-5 w-5" /> : <Icon className="h-5 w-5" />}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className={`font-display text-sm font-bold ${active || done || open ? "text-white" : "text-revo-ice/60"}`}>{s.label}</p>
                      <AnimatePresence>
                        {active && (
                          <motion.p initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} className="text-xs text-revo-ice">
                            {s.detail}
                          </motion.p>
                        )}
                      </AnimatePresence>
                    </div>
                    <ChevronDown className={`h-4 w-4 shrink-0 text-revo-ice/50 transition-transform duration-300 ${open ? "rotate-180 text-revo-green" : ""}`} />
                  </button>
                  <AnimatePresence initial={false}>
                    {open && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
                        className="overflow-hidden"
                        data-testid={`activation-step-panel-${s.key}`}
                      >
                        <div className="border-t border-white/10 px-4 pb-4 pt-3">
                          <p className="text-[11px] font-bold uppercase tracking-wider text-revo-green">What it means</p>
                          <p className="mt-1 text-xs leading-relaxed text-revo-ice/80">{s.meaning}</p>
                          <p className="mt-3 text-[11px] font-bold uppercase tracking-wider text-revo-green">What happens next</p>
                          <p className="mt-1 text-xs leading-relaxed text-revo-ice/80">{s.next}</p>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>
          <p className="mt-3 text-xs text-revo-ice/50">Tap any step to see what it means and what happens next.</p>

          <div className="mt-8 flex gap-3">
            <button
              onClick={start}
              disabled={running}
              data-testid="activation-start-btn"
              className="rounded-full bg-revo-green px-6 py-3 text-sm font-bold text-navy transition-transform duration-200 hover:scale-[1.03] disabled:opacity-50"
            >
              {running ? "Activating…" : step === STEPS.length - 1 ? "Run again" : "Scan the serial"}
            </button>
            {step > -1 && !running && (
              <button onClick={reset} data-testid="activation-reset-btn" className="flex items-center gap-2 rounded-full border border-white/15 px-5 py-3 text-sm font-semibold text-revo-ice transition-colors hover:text-white">
                <RotateCcw className="h-4 w-4" /> Reset
              </button>
            )}
          </div>
        </div>

        {/* Right: device + meters */}
        <div className="relative flex flex-col items-center justify-center gap-8 p-7 md:p-10">
          <div className="relative flex h-44 w-44 items-center justify-center">
            {step >= 2 && (
              <>
                {[0, 0.9].map((d) => (
                  <span key={d} className="absolute inset-0 rounded-full border border-revo-green/50" style={{ animation: `ring-expand 2.4s ${d}s ease-out infinite` }} />
                ))}
              </>
            )}
            {/* battery */}
            <div className="relative z-10 flex h-40 w-24 flex-col justify-end overflow-hidden rounded-2xl border-2 border-white/20 bg-navy p-1.5">
              <motion.div
                className="rounded-lg bg-gradient-to-t from-revo-green to-revo-green/70"
                animate={{ height: `${battery}%` }}
                transition={{ ease: "easeOut" }}
              />
              <span className="absolute inset-0 flex items-center justify-center font-display text-2xl font-extrabold text-white drop-shadow" data-testid="activation-battery-pct">
                {battery}%
              </span>
            </div>
          </div>

          <div className="grid w-full max-w-xs grid-cols-2 gap-3">
            <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
              <p className="text-[11px] uppercase tracking-wider text-revo-ice/60">Live meter</p>
              <p className="font-display text-2xl font-extrabold text-revo-green" data-testid="activation-wh-meter">{wh.toFixed(1)}</p>
              <p className="text-[11px] text-revo-ice/60">watt-hours</p>
            </div>
            <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
              <p className="text-[11px] uppercase tracking-wider text-revo-ice/60">Serial</p>
              <p className="font-display text-lg font-extrabold text-white">{SERIAL}</p>
              <p className="text-[11px] text-revo-ice/60">enrolled</p>
            </div>
          </div>

          <AnimatePresence>
            {step >= 4 && (
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                className="w-full max-w-xs rounded-xl border border-revo-green/30 bg-revo-green/5 p-4"
                data-testid="activation-invoice"
              >
                <div className="flex items-center justify-between text-sm">
                  <span className="text-revo-ice">Metered usage · {SERIAL}</span>
                  <span className="font-display font-bold text-white">{wh.toFixed(1)} Wh</span>
                </div>
                <div className="mt-1 flex items-center justify-between text-xs text-revo-ice/60">
                  <span>Added to your monthly account statement</span>
                  <span className="text-revo-green">$0.01</span>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}
